import { compact } from 'lodash';
import type { OrderForm_AssortmentFragment } from '../../graphql';
import type {
  AssortmentExclusionKey,
  UseAssortmentExclusionsValue,
  AssortmentExclusionItem,
} from '../hooks/useAssortmentExclusions';
import type { ClientId } from './ClientId';
import { getEventOrder } from './Core';
import type { Events } from './TimeData';
import type { Vessel } from './Vessel';

/**
 * Returns a map of assortments by shipment ids and array of shipment ids with same order they appear on timeline
 * @param events
 */
function getAssortmentsByShipmentId(events: Events[]) {
  const assortmentsByShipmentId = new Map<
    ClientId | null,
    OrderForm_AssortmentFragment[]
  >();
  for (const ev of events) {
    const shipmentId = getEventOrder(ev)?.shipmentId;
    const assortment = getEventOrder(ev)?.assortmentByAssortmentId;
    const existingAssortments = shipmentId
      ? assortmentsByShipmentId.get(shipmentId)
      : null;
    const isDuplicate = existingAssortments?.some(
      (a) => a.name === assortment?.name,
    );
    if (shipmentId !== undefined && assortment && !isDuplicate) {
      assortmentsByShipmentId.set(shipmentId, [
        ...(existingAssortments ?? []),
        assortment,
      ]);
    }
  }
  const shipmentIds = Array.from(assortmentsByShipmentId.keys());
  return { assortmentsByShipmentId, shipmentIds };
}
/**
 * Iterates possible exclusion rules
 * @param prev array of previous assortments that has been used in previous shipments
 * @param curr array of current assortments that has been used in current shipment
 */
function* iterateAssortmentExclusionRules(
  prev: OrderForm_AssortmentFragment[],
  curr: OrderForm_AssortmentFragment[],
): IterableIterator<AssortmentExclusionKey> {
  for (const prevItem of prev) {
    const prevGroup = prevItem.assortmentGroupByAssortmentGroup?.name;
    const prevAssortment = prevItem.name;
    for (const currItem of curr) {
      const currGroup = currItem.assortmentGroupByAssortmentGroup?.name;
      const currAssortment = currItem.name;
      if (prevAssortment && currAssortment) {
        yield `A${prevAssortment}-A${currAssortment}`;
      }
      if (prevGroup && currGroup) {
        yield `G${prevGroup}-G${currGroup}`;
      }
      if (prevGroup && currAssortment) {
        yield `G${prevGroup}-A${currAssortment}`;
      }
      if (prevAssortment && currGroup) {
        yield `A${prevAssortment}-G${currGroup}`;
      }
    }
  }
}
/**
 * Returns a map of assortment exclusions by shipment id
 * @param vessel
 * @param events
 * @param assortmentExclusions
 */

export function getAssortmentExclusionsByShipmentId(
  vessel: Vessel,
  events: Events[],
  assortmentExclusions: UseAssortmentExclusionsValue,
) {
  const vesselAttributes = compact([
    ...(vessel.vesselAttributesAssociationsByShipId?.nodes.map(
      (n) => n?.vesselAttributeByVesselAttributeId,
    ) ?? []),
    vessel.vesselAttributeByPrimaryAttributeId,
  ]);

  const { voyageExclusions, vesselExclusionsMap } = assortmentExclusions;
  const effectiveVesselExclusions = vesselAttributes.flatMap(
    (a) => vesselExclusionsMap.get(a.id) ?? [],
  );

  const assortmentExclusionsByShipmentId = new Map<
    ClientId | null,
    AssortmentExclusionItem[]
  >();

  const { assortmentsByShipmentId, shipmentIds } =
    getAssortmentsByShipmentId(events);

  for (const shipmentId of shipmentIds) {
    const addToResult = (value: AssortmentExclusionItem): void => {
      const existingValue = assortmentExclusionsByShipmentId.get(shipmentId);
      const isDuplicate = existingValue?.some((i) => i === value);
      if (!isDuplicate) {
        assortmentExclusionsByShipmentId.set(shipmentId, [
          ...(existingValue ?? []),
          value,
        ]);
      }
    };

    const currAssortments = assortmentsByShipmentId.get(shipmentId) ?? [];
    voyageExclusions.forEach((value, key) => {
      const voyages = value?.voyages ?? 0;

      const prevAssortments: OrderForm_AssortmentFragment[] = [];

      const shipmentIdIdx = shipmentIds.indexOf(shipmentId);
      const prevShipmentIds =
        shipmentIdIdx > 0
          ? shipmentIds.slice(0, shipmentIdIdx).slice(-voyages)
          : [];

      for (const prevShipmentId of prevShipmentIds) {
        const prevAssortment = assortmentsByShipmentId.get(prevShipmentId);

        prevAssortment?.forEach((pv) => {
          if (!prevAssortments.some((i) => i.id === pv.id)) {
            prevAssortments.push(pv);
          }
        });
      }

      const rules = iterateAssortmentExclusionRules(
        prevAssortments,
        currAssortments,
      );

      for (const rule of rules) {
        if (rule === key) {
          addToResult(value);
          break;
        }
      }
    });
    for (const vesselExclusion of effectiveVesselExclusions) {
      const {
        exclusion: { __typename, id },
      } = vesselExclusion;
      for (const assortment of currAssortments) {
        if (
          (__typename === 'Assortment' && id === assortment.id) ||
          (__typename === 'AssortmentGroup' &&
            id === assortment.assortmentGroupByAssortmentGroup?.id)
        ) {
          addToResult(vesselExclusion);
        }
      }
    }
  }
  return assortmentExclusionsByShipmentId;
}
